import React, { useEffect, useState } from "react";
import { Card, Badge, Button, EmptyState, LoadingState, ErrorState, Field, Input } from "@/components/design-system";
import { adminModerationSummary, adminListSubmissions, adminModerate } from "@/lib/campaigns";
import { formatApiError } from "@/lib/api";

const cn = (...xs) => xs.filter(Boolean).join(" ");
const TABS = ["approved", "hidden"];

function AnswerRow({ a }) {
  const empty = a.value === null || a.value === undefined || a.value === "";
  return (
    <div className="border-t border-[#0B1F3A]/10 pt-2 mt-2 first:mt-0 first:pt-0 first:border-0">
      <div className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/50">{a.question_type}</div>
      <div className="text-xs font-semibold text-[#0B1F3A]">{a.question_text}</div>
      <div className="text-xs text-[#0B1F3A]/80 mt-0.5">{empty ? "—" : String(a.value)}</div>
    </div>
  );
}

export default function AdminModeration() {
  const [tab, setTab] = useState("approved");
  const [summary, setSummary] = useState(null);
  const [state, setState] = useState({ status: "loading", items: [], err: "" });
  const [reason, setReason] = useState({});
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setState((s) => ({ ...s, status: "loading" }));
    try {
      const [sum, subs] = await Promise.all([adminModerationSummary(), adminListSubmissions(tab)]);
      setSummary(sum);
      setState({ status: "ready", items: subs.items, err: "" });
    } catch (err) { setState({ status: "error", items: [], err: formatApiError(err) }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const onHide = async (s) => {
    setBusy(s.id);
    try {
      const next = await adminModerate(s.id, "hidden", reason[s.id] || "");
      setState((st) => ({ ...st, items: st.items.filter((x) => x.id !== next.id) }));
    } catch (err) { alert(formatApiError(err)); }
    finally { setBusy(null); }
  };
  const onRestore = async (s) => {
    setBusy(s.id);
    try {
      const next = await adminModerate(s.id, "approved", null);
      setState((st) => ({ ...st, items: st.items.filter((x) => x.id !== next.id) }));
    } catch (err) { alert(formatApiError(err)); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="admin-moderation-page">
      <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Administrator</p>
      <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">Moderation queue</h1>
      <p className="text-[#0B1F3A]/70 mt-2 max-w-2xl">Review all community submissions. Hide anything abusive, off-topic, or low-quality — the reason you provide is stored in the audit trail.</p>

      {summary && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="!p-5"><div className="text-[11px] font-bold uppercase tracking-widest text-[#0B1F3A]/60">Users</div><div className="font-[Montserrat] font-extrabold text-3xl mt-2">{summary.users}</div></Card>
          <Card className="!p-5"><div className="text-[11px] font-bold uppercase tracking-widest text-[#0B1F3A]/60">Products</div><div className="font-[Montserrat] font-extrabold text-3xl mt-2">{summary.products}</div></Card>
          <Card className="!p-5"><div className="text-[11px] font-bold uppercase tracking-widest text-[#0B1F3A]/60">Open campaigns</div><div className="font-[Montserrat] font-extrabold text-3xl mt-2">{summary.open_campaigns}</div></Card>
          <Card className="!p-5"><div className="text-[11px] font-bold uppercase tracking-widest text-[#0B1F3A]/60">Approved</div><div className="font-[Montserrat] font-extrabold text-3xl mt-2">{summary.submissions_approved}</div></Card>
          <Card className="!p-5"><div className="text-[11px] font-bold uppercase tracking-widest text-[#0B1F3A]/60">Hidden</div><div className="font-[Montserrat] font-extrabold text-3xl mt-2">{summary.submissions_hidden}</div></Card>
        </div>
      )}

      <div className="mt-8 inline-flex items-center gap-1 rounded-full border border-[#0B1F3A]/10 p-1 bg-white">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`moderation-tab-${t}`}
            onClick={() => setTab(t)}
            className={cn("px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors", tab === t ? "bg-[#0B1F3A] text-white" : "text-[#0B1F3A]/70")}
          >{t}</button>
        ))}
      </div>

      <div className="mt-6">
        {state.status === "loading" && <LoadingState label="Loading queue…" />}
        {state.status === "error" && <ErrorState detail={state.err} />}
        {state.status === "ready" && state.items.length === 0 && (
          <EmptyState title="Queue is empty" description={tab === "approved" ? "No approved submissions yet." : "No submissions have been hidden."} />
        )}
        {state.status === "ready" && state.items.length > 0 && (
          <div className="space-y-4" data-testid="moderation-list">
            {state.items.map((s) => (
              <Card key={s.id} data-testid={`moderation-item-${s.id}`} className="!p-6">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-sm font-semibold text-[#0B1F3A]">{s.reviewer_name}</div>
                    <div className="text-xs text-[#0B1F3A]/60">{new Date(s.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={s.moderation_status === "hidden" ? "admin" : "active"}>{s.moderation_status}</Badge>
                    {s.helpful && <Badge variant="active">★ Helpful</Badge>}
                  </div>
                </div>
                <div className="mt-4">{s.answers.map((a) => <AnswerRow key={a.question_id} a={a} />)}</div>
                {s.moderation_status === "hidden" && s.hidden_reason && (
                  <div className="mt-4 text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2"><b>Hidden reason:</b> {s.hidden_reason}</div>
                )}
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  {tab === "approved" ? (
                    <>
                      <Field label="Reason (optional)"><Input data-testid={`moderation-reason-${s.id}`} value={reason[s.id] || ""} onChange={(e) => setReason((r) => ({ ...r, [s.id]: e.target.value }))} maxLength={280} placeholder="Why is this being hidden?" /></Field>
                      <Button data-testid={`moderation-hide-${s.id}`} variant="danger" onClick={() => onHide(s)} disabled={busy === s.id}>Hide submission</Button>
                    </>
                  ) : (
                    <Button data-testid={`moderation-restore-${s.id}`} variant="primary" onClick={() => onRestore(s)} disabled={busy === s.id}>Restore</Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
